import { css } from 'aphrodite';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useSupportTicketQuery } from '../../../queries/Support/useSupportTicketQuery';
import { AssigneeIcon } from '../../../web/components/svgs/icons/AssigneeIcon';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import {
	AdminDrawerToolbar,
	AppDrawerContent,
	AppDrawerProvider,
	AppDrawerSeparator,
	AppDrawerToolbarButton,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { AdminAppAssistantChat } from '../../../web/containers/templates/AssistantChat';
import { SupportNewTicketDrawerButton } from '../../components/supportShared/SupportNewTicketDrawerButton';
import type { NewTicketMode } from '../../components/supportShared/NewTicketModal/newTicketDraft';
import { inboxStyles as s } from './styles/inboxStyles';
import { TicketDetailsBody } from './TicketDetailsBody';
import { TICKET_FIELD_SHORTCUTS, type TicketFieldShortcut, useTicketShortcuts } from './useTicketShortcuts';

type RightTab = 'assistant' | 'details';

interface RightPanelProps {
	ticketId: string;
	/** Default drawer panel for a fresh selection. The support desk default is Details. */
	defaultTab?: RightTab;
	/** Navigate the inbox to another ticket from the previous-conversations preview modal. */
	onSelectTicket?: (ticketId: string) => void;
	/** Opens the customer picker for customerless tickets. */
	onChangeCustomer?: () => void;
	/** Called after the ticket leaves the agent's plate (closed, spam, archived, or assigned). */
	onTicketLeftView?: () => void;
	onNewTicket?: (mode: NewTicketMode) => void;
}

const SUPPORT_INBOX_DRAWER_PERSIST_KEY = 'support-inbox-active-drawer-key';
const DETAILS_DRAWER_VALUE = 'support-ticket-details';
const ASSISTANT_DRAWER_VALUE = 'support-ticket-assistant';

const getDrawerValue = (tab: RightTab) => (tab === 'assistant' ? ASSISTANT_DRAWER_VALUE : DETAILS_DRAWER_VALUE);

/**
 * Support inbox drawer. This intentionally uses the product AppDrawer primitives, but mounts them
 * inside the support desk page grid instead of `AdminDashboardPageLayout.sidebarChildren`; that keeps
 * the admin masthead/user-menu anchored while the drawer opens and closes. Phones do not use this
 * panel at all — see {@link PhoneTicketStage}.
 */
export const RightPanel: React.FC<RightPanelProps> = ({
	ticketId,
	defaultTab = 'details',
	onSelectTicket,
	onChangeCustomer,
	onTicketLeftView,
	onNewTicket,
}) => {
	const isOverlayDrawer = useMediaQuery({ maxWidth: 1279 });
	const [drawerValue, setDrawerValue] = React.useState(() => getDrawerValue(defaultTab));
	const [shortcutField, setShortcutField] = React.useState<TicketFieldShortcut | null>(null);

	// This panel is mounted only with a ticket open on the desk, so the keys always have a subject.
	// They also bring Details forward: on a narrow layout it is a closed overlay, and behind the
	// Assistant it is mounted but hidden — either way the raised picker needs to be on screen.
	useTicketShortcuts({
		enabled: true,
		shortcuts: TICKET_FIELD_SHORTCUTS,
		onShortcut: shortcut => {
			setDrawerValue(DETAILS_DRAWER_VALUE);
			setShortcutField(shortcut);
		},
	});

	React.useEffect(() => setShortcutField(null), [ticketId]);

	// Narrow layouts keep details as a temporary overlay. Desktop continues to open the persistent details drawer.
	React.useEffect(
		() => setDrawerValue(isOverlayDrawer ? '' : getDrawerValue(defaultTab)),
		[ticketId, defaultTab, isOverlayDrawer]
	);

	const { data: ticket } = useSupportTicketQuery(ticketId, { refetchInterval: false });

	return (
		<AppDrawerProvider value={drawerValue} onValueChange={setDrawerValue} persistKey={SUPPORT_INBOX_DRAWER_PERSIST_KEY}>
			<SideDrawer styles={[s.supportDrawer]}>
				<AppDrawerContent value={DETAILS_DRAWER_VALUE} styles={[s.supportDrawerContent]}>
					<div className={css(s.rightContent)}>
						<TicketDetailsBody
							ticketId={ticketId}
							isVisible={drawerValue === DETAILS_DRAWER_VALUE}
							onSelectTicket={onSelectTicket}
							onChangeCustomer={onChangeCustomer}
							onTicketLeftView={onTicketLeftView}
							shortcutField={shortcutField}
							onShortcutFieldChange={setShortcutField}
						/>
					</div>
				</AppDrawerContent>

				{/* Lazy-mount: AssistantChat fires AI-conversation and account fetches on mount, so
				    keeping it mounted behind the Details tab costs those requests for every ticket
				    selection even when the assistant is never opened. */}
				<AppDrawerContent value={ASSISTANT_DRAWER_VALUE} unmountOnExit styles={[s.supportDrawerContent]}>
					{ticket?.accountId ? (
						<div className={css(s.rightAssistantContent)}>
							<AdminAppAssistantChat
								key={ticket.accountId}
								impersonationContext={{ account: { id: ticket.accountId } }}
							/>
						</div>
					) : (
						<div className={css(s.empty)}>
							<SparklesIcon width={28} height={28} />
							<div>Connect this ticket to an account to use Assistant.</div>
						</div>
					)}
				</AppDrawerContent>

				<AdminDrawerToolbar>
					{onNewTicket && (
						<>
							<SupportNewTicketDrawerButton onSelect={onNewTicket} />
							<AppDrawerSeparator />
						</>
					)}
					<AppDrawerToolbarButton value={ASSISTANT_DRAWER_VALUE} tooltip='AI Assistant'>
						<SparklesIcon />
					</AppDrawerToolbarButton>
					<AppDrawerSeparator />
					<AppDrawerToolbarButton value={DETAILS_DRAWER_VALUE} tooltip='Ticket details'>
						<AssigneeIcon />
					</AppDrawerToolbarButton>
				</AdminDrawerToolbar>
			</SideDrawer>
		</AppDrawerProvider>
	);
};
